/**
 * WEB-2330 — где живёт кабинет относительно апекса сборки.
 *
 * До переезда кабинет жил на КОРНЕ апекса (`testfunnelsdev.click`). На
 * subster.ai корень зарезервирован под лендинг, а кабинет живёт на
 * `app.subster.ai` (решение владельца 22.09, docs/domain-migration/PLAN.md §0).
 * Стенд (`frontend-staging-e412.up.railway.app`) остаётся «кабинет на корне»:
 * у него переменная хоста кабинета приравнена к апексу.
 *
 * Чистые правила без `import.meta` — их зовёт конфиг сборки
 * (`config/public-host.ts`) и вход кабинета (`main.tsx`), а под Jest они
 * проверяются напрямую (сам конфиг там подменён).
 *
 * Хост кабинета обязан быть самим апексом или его поддоменом: всё прочее
 * фронт и раздатчик (Caddyfile) считают чужим доменом и отдают воронку.
 */

/** Апекс по умолчанию — боевой. Стенд задаёт свой переменной сборки. */
export const DEFAULT_PUBLIC_APEX = 'subster.ai';

/** Нижний регистр, без пробелов по краям и без завершающей точки (FQDN). */
function normalizeHost(host: string): string {
  return host.trim().toLowerCase().replace(/\.$/, '');
}

/** Значение переменной сборки; пустое (ARG без значения) = «не задано». */
function configured(raw: string | undefined | null): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const value = normalizeHost(raw);
  return value === '' ? undefined : value;
}

/** Апекс сборки из `VITE_PUBLIC_APEX`. */
export function resolvePublicApex(raw: string | undefined | null): string {
  return configured(raw) ?? DEFAULT_PUBLIC_APEX;
}

/** Хост кабинета из `VITE_APP_HOST`; по умолчанию `app.<апекс>`. */
export function resolveAppHost(raw: string | undefined | null, apex: string): string {
  return configured(raw) ?? `app.${normalizeHost(apex)}`;
}

interface LocationLike {
  readonly hostname: string;
  readonly pathname: string;
  readonly search: string;
  readonly hash: string;
}

/**
 * Куда увести человека, зашедшего на САМ корень апекса, когда кабинет живёт не
 * там. `null` — никуда: хост не корень апекса, либо кабинет и есть корень
 * (стенд — прежнее поведение). Путь, запрос и якорь сохраняются, чтобы старые
 * закладки (`/settings?tab=security`) открывали ту же страницу кабинета.
 */
export function apexRootRedirectUrl(
  location: LocationLike,
  apex: string,
  appHost: string,
): string | null {
  const host = normalizeHost(location.hostname);
  const normalizedApex = normalizeHost(apex);
  const normalizedAppHost = normalizeHost(appHost);
  if (!normalizedApex || !normalizedAppHost) return null;
  if (normalizedApex === normalizedAppHost) return null;
  if (host !== normalizedApex) return null;
  const path = location.pathname || '/';
  return `https://${normalizedAppHost}${path}${location.search}${location.hash}`;
}
